// TrenerAbsenceCard — sekce "Plánované absence" trenéra pro dashboard.
// Sdílí UI s rozvrhem vzoru (AbsenceSection + AbsenceModal z RozvrhVzorGrid),
// jen má vlastní lehký fetch (GET /api/trener-rozvrh-absence?pilot=) + add/delete.
//
// Pravidla (shodná s rozvrhem vzoru):
//   - management (owner/manager/head_coach): smí přidat i zpětně, bez minima
//   - trenér (vlastní účet): jen ≥14 dní dopředu, jen vlastní

import React, { useState, useEffect, useCallback } from "react";
import { apiFetch } from "../lib/api.js";
import { getCurrentUser } from "../lib/auth.js";
import { AbsenceSection, AbsenceModal } from "./RozvrhVzorGrid.jsx";
import { toast } from "./Toast.jsx";

export default function TrenerAbsenceCard({ activeTrener }) {
  const user = getCurrentUser();
  const isManagement = user && ["owner", "manager", "head_coach"].includes(user.role);
  const isOwnTrener = user?.role === "trener" && user?.linkedTrener === activeTrener;
  const canEditAbsence = isManagement || isOwnTrener;
  const canBackdateAbsence = isManagement;
  const minAbsenceDaysAhead = isManagement ? 0 : 14;

  const [absences, setAbsences] = useState([]);
  const [showModal, setShowModal] = useState(false);

  const load = useCallback(async () => {
    if (!activeTrener) return;
    try {
      const r = await apiFetch(`/api/trener-rozvrh-absence?pilot=${encodeURIComponent(activeTrener)}`);
      const j = await r.json();
      setAbsences(Array.isArray(j.absence) ? j.absence : []);
    } catch { /* ticho */ }
  }, [activeTrener]);

  useEffect(() => { load(); }, [load]);

  const handleAdd = useCallback(async (payload) => {
    setShowModal(false);
    try {
      const r = await apiFetch("/api/trener-rozvrh-absence", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ pilot: activeTrener, userRole: user?.role, ...payload }),
      });
      if (!r.ok) { const j = await r.json().catch(() => ({})); throw new Error(j.error || "Chyba"); }
    } catch (e) { toast(`Chyba uložení absence: ${e.message}`, "error"); }
    finally { load(); }
  }, [activeTrener, user, load]);

  const handleDelete = useCallback(async (rowNum) => {
    try {
      const r = await apiFetch("/api/trener-rozvrh-absence", {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: rowNum, rowNum }),
      });
      if (!r.ok) throw new Error("Chyba");
    } catch (e) { toast(`Chyba mazání absence: ${e.message}`, "error"); }
    finally { load(); }
  }, [load]);

  if (!activeTrener) return null;

  return (
    <>
      <AbsenceSection
        absences={absences}
        editable={canEditAbsence}
        onAdd={() => setShowModal(true)}
        onDelete={handleDelete}
      />
      {showModal && (
        <AbsenceModal
          canBackdate={canBackdateAbsence}
          minDaysAhead={minAbsenceDaysAhead}
          onClose={() => setShowModal(false)}
          onSave={handleAdd}
        />
      )}
    </>
  );
}
